import { useEffect, useState } from 'react'

import { AccessProfileFilters } from './components/AccessProfileFilters'
import { AccessProfileModal } from './components/AccessProfileModal'
import { AddNewProfileModal } from './components/AddNewProfileModal'
import { getPagePermissions } from '@hooks/useUser/provider'
import { initialPagination, type PaginationParams } from '@components/shared/Table/components/Pagination/types'
import { PageIds } from '@hooks/useLayout/types'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseAccessProfileGrid } from './helpers/parseAccessProfileGrid'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { Table } from '@components/shared/Table'
import { ReturnModal } from '@components/modals/ReturnModal'
import { TrashSimpleIcon } from '@phosphor-icons/react'
import { useAccessProfile } from './context'
import { useUser } from '@hooks/useUser'

import type { ProfileItemProps } from './context/types'
import type { SortProps } from '@components/shared/Table/types'
import type { PermissionsItemProps } from './context/types'

export const AccessProfile = () => {
  const { profilesData, isProfilesLoading, getProfiles, deleteProfile, isDeleteProfileLoading } = useAccessProfile()
  const { userPermissionsData } = useUser()

  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)

  const [openAccessProfileModal, setOpenAccessProfileModal] = useState(false)
  const [accessProfileFormData, setAccessProfileFormData] = useState<ProfileItemProps | null>(null)
  const [openAddNewProfileModal, setOpenAddNewProfileModal] = useState(false)
  const [openDeleteProfileModal, setOpenDeleteProfileModal] = useState(false)

  const handleProfileDetails = (data: ProfileItemProps) => {
    setOpenAccessProfileModal(true)
    setAccessProfileFormData(data)
  }

  const handleDeleteProfile = (data: ProfileItemProps) => {
    setOpenDeleteProfileModal(true)
    setAccessProfileFormData(data)
  }

  const handleGetProfiles = (
    params: PaginationParams | null,
    formData?: ProfileItemProps | null,
    sortData?: SortProps
  ) => {
    getProfiles({
      ...sortData,
      ...(formData || accessProfileFormData),
      page: params?.page || initialPagination.PAGE,
      itemsPerPage: params?.itemsPerPage || profilesData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setAccessProfileFormData(formData)
  }

  useEffect(() => {
    const filteredProfile = profilesData?.items?.find((item) => item.id === accessProfileFormData?.id)

    !!filteredProfile && setAccessProfileFormData(filteredProfile)
  }, [profilesData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.ACCESS_PROFILE)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  const handleInitialActionsFromScreen = () => {
    handleGetProfiles(null)
  }

  useEffect(() => {
    handleInitialActionsFromScreen()
  }, [])

  return (
    <>
      <PageWrapper breadcrumbData={[{ label: 'Perfil de Acesso' }]}>
        <AccessProfileFilters
          onFilter={(formData) => handleGetProfiles(null, formData as unknown as ProfileItemProps)}
        />

        <Table
          data={parseAccessProfileGrid({
            data: profilesData?.items || [],
            onSeeMore: handleProfileDetails,
            onRemove: handleDeleteProfile
          })}
          fixedRow={{
            element: <RowTableTotal2 data={[{ label: 'Total Registros', value: profilesData?.totalItems || 0 }]} />
          }}
          footer={{
            actionButton: {
              onClick: () => setOpenAddNewProfileModal(true),
              labelButton: 'Adicionar Perfil',
              disabled: !permissions?.canCreate
            },
            pagination: {
              currentPage: profilesData?.currentPage,
              itemsPerPage: profilesData?.itemsPerPage,
              totalItems: profilesData?.totalItems,
              totalPages: profilesData?.totalPages,
              onPageChange: (page) => handleGetProfiles({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetProfiles({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetProfiles(null, null, sortData)}
          isLoading={isProfilesLoading}
        />
      </PageWrapper>

      <AccessProfileModal
        data={accessProfileFormData}
        open={openAccessProfileModal}
        onClose={setOpenAccessProfileModal}
      />

      <AddNewProfileModal open={openAddNewProfileModal} onClose={setOpenAddNewProfileModal} />

      <ReturnModal
        open={openDeleteProfileModal}
        headerIcon={<TrashSimpleIcon size={24} weight="bold" />}
        message="Você tem certeza de que deseja excluir este perfil?"
        headerTitle="Excluir Perfil"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={() => {
          setOpenDeleteProfileModal(false)
          setAccessProfileFormData(null)
        }}
        onConfirm={() =>
          deleteProfile(accessProfileFormData?.id!).then((isSuccess) => {
            if (isSuccess) {
              setOpenDeleteProfileModal(false)
              setAccessProfileFormData(null)
            }
          })
        }
        isLoading={isDeleteProfileLoading}
      />
    </>
  )
}
